import React, { useState, useRef, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Alert,
  Grid,
  Typography,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
} from "@mui/material";
import {
  ArrowRightAlt as ArrowRightAltIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";

import {
  textColorHandler,
  hasPositionBarrier,
  getTerminalCallArrivalStateContainerIds,
  RenderLabels,
  getTerminalCallLoadingContainerIds,
} from "../Helpers.tsx";
import { selectedFilteredVessel } from "../../selectors/filterSelector.js";
import { updateSelectedContainer } from "../../data/selectedcontainers.js";
import { Container } from "../../types/Container";
import { TerminalCall } from "../../types/TerminalCall.ts";
import LabelDots from "../partial/LabelDots.tsx";
import { Vessel } from "../../types/Vessel.ts";

const NO_CONTAINER_COLOR = "#FFFFFF";
const DEFAULT_CONTAINER_COLOR = "#FFE4C4";
const PLAN_TYPE_LOADING = "loadingTerminalCall";
const PLAN_TYPE_UNLOADING = "unloadingTerminalCall";

interface TerminalCallGridProps {
  selectedTerminalCall: TerminalCall;
  planType?: string;
}

const TerminalCallGrid: React.FC<TerminalCallGridProps> = ({
  selectedTerminalCall,
  planType = PLAN_TYPE_LOADING,
}) => {
  const dispatch = useDispatch();
  const [selectedLayer, setSelectedLayer] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [showRemainingCargo, setShowRemainingCargo] = useState(true);
  const [selectedVessel, setSelectedVessel] = useState<Vessel | undefined>(undefined);
  const [activeContainer, setActiveContainer] = useState<Container | null>(null);

  const selectedVesselForPlanner = useSelector(selectedFilteredVessel);
  const allContainers: Container[] = useSelector(
    (state: any) => state.selectedContainers.data
  );

  const touchStartX = useRef<number>(0);

  const selectedVesselForPlannerHandler = (
    event: React.MouseEvent<HTMLElement>,
    vessel: Vessel
  ) => {
    setSelectedVessel(vessel);
  };

  useEffect(() => {
    if (!selectedVessel) {
      setSelectedVessel(selectedVesselForPlanner);
    }
  }, [selectedVesselForPlanner]);

  const containerColorHandler = (container: Container | undefined) => {
    if (!container) return NO_CONTAINER_COLOR;

    if (planType === PLAN_TYPE_UNLOADING) {
      return (
        container.loadingTerminalCall?.terminal?.color ||
        container.loadingTerminal?.color ||
        DEFAULT_CONTAINER_COLOR
      );
    }

    if (planType === PLAN_TYPE_LOADING) {
      return (
        container.unloadingTerminalCall?.terminal?.color ||
        container.unloadingTerminal?.color ||
        DEFAULT_CONTAINER_COLOR
      );
    }

    return DEFAULT_CONTAINER_COLOR;
  };

  const containerData = useMemo<Container[]>(() => {
    if (!selectedTerminalCall) return [];

    if (planType === PLAN_TYPE_LOADING) {
      const unloadingIds = (selectedTerminalCall.unloadingContainers || [])
        .map((c: any) => c["@id"])
        .filter((x: any) => !!x);

      const arrivalIds =
        getTerminalCallArrivalStateContainerIds(selectedTerminalCall) || [];
      const filteredArrival = arrivalIds.filter((id) => !unloadingIds.includes(id));

      const loadingIds = (selectedTerminalCall.loadingContainers || [])
        .map((c: any) => c["@id"])
        .filter((x: any) => !!x);
      const filteredLoading = loadingIds.filter((id) => !unloadingIds.includes(id));

      const allowedIds = [...filteredArrival, ...filteredLoading];
      return allContainers.filter((c) => allowedIds.includes(c["@id"]));
    } else {
      const unloadingIds = (selectedTerminalCall.unloadingContainers || [])
        .map((c: any) => c["@id"])
        .filter((x: any) => !!x);
      return allContainers.filter((c) => unloadingIds.includes(c["@id"]));
    }
  }, [allContainers, selectedTerminalCall, planType]);

  const findContainerByPosition = (
    position: (string | number)[]
  ): Container | undefined => {
    const explicitList =
      planType === PLAN_TYPE_UNLOADING
        ? (selectedTerminalCall.unloadingContainers || []).map((c: any) => c["@id"])
        : (selectedTerminalCall.loadingContainers || []).map((c: any) => c["@id"]);

    const arrivalList = getTerminalCallLoadingContainerIds(selectedTerminalCall).filter(
      (id) =>
        !(selectedTerminalCall.unloadingContainers || [])
          .map((c: any) => c["@id"])
          .includes(id)
    );

    if (selectedVessel) {
      position.push(selectedVessel["@id"].replace("/api/vessels/", ""));
    }

    return containerData.find((c) => {
      if (!c.position) return false;
      if (JSON.stringify(c.position) !== JSON.stringify(position)) return false;
      return explicitList.includes(c["@id"]) || arrivalList.includes(c["@id"]);
    });
  };

  const findContainerId = (container: Container): Container | undefined => {
    return containerData.find((c) => c["@id"] === container["@id"]);
  };

  const isContainerFromArrivalContainers = (
    container: Container | undefined
  ): boolean => {
    if (!container) return false;
    return (
      planType !== PLAN_TYPE_UNLOADING &&
      getTerminalCallArrivalStateContainerIds(selectedTerminalCall).includes(container["@id"])
    );
  };

  const clickTimer = useRef<number | null>(null);
  const lastTap = useRef<number>(0);

  const RenderGridCell = (
    layer: number,
    xAxis: number,
    yAxis: number,
    gridCount: number,
    grids: number[],
  ) => {
    const ghostGrid = (gridCount === 2 && (yAxis === 1 || yAxis === 4)) || (gridCount === 3 && (yAxis === 1 || yAxis === 5));
    let container = findContainerByPosition([layer, xAxis, yAxis]);
    let isAnArrival = isContainerFromArrivalContainers(container);
    let color = container ? containerColorHandler(container) : NO_CONTAINER_COLOR;

    if (!showRemainingCargo && isAnArrival && container) {
      container = undefined;
      isAnArrival = false;
      color = NO_CONTAINER_COLOR;
    }

    const textColor = textColorHandler(color);

    const handleDoubleAction = () => {
      if (!container) return;

      if (planType === PLAN_TYPE_LOADING) {
        const newLoadedDate = container.loadedDate ? null : new Date().toISOString();
        dispatch(
          updateSelectedContainer({
            ...container,
            loadedDate: newLoadedDate,
          })
        );
      } else {
        const newUnloadedDate = container.unloadedDate ? null : new Date().toISOString();
        dispatch(
          updateSelectedContainer({
            ...container,
            unloadedDate: newUnloadedDate,
          })
        );
      }
    };

    const handleSingleAction = () => {
      if (container && isAnArrival) return;

      if (container) {
        setActiveContainer(findContainerId(container) || null);
        setOpenDialog(true);
      }
    };

    const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (container && isAnArrival) return;

      e.stopPropagation();
      if (clickTimer.current) {
        window.clearTimeout(clickTimer.current);
        clickTimer.current = null;
      }
      clickTimer.current = window.setTimeout(() => {
        handleSingleAction();
        clickTimer.current = null;
      }, 250);
    };

    const onDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (container && isAnArrival) return;

      if (clickTimer.current !== null) {
        window.clearTimeout(clickTimer.current);
        clickTimer.current = null;
      }
      e.stopPropagation();
      handleDoubleAction();
    };

    const onTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
      if (container && isAnArrival) return;

      e.stopPropagation();

      const currentTime = new Date().getTime();
      const tapGap = currentTime - lastTap.current;
      if (tapGap < 300 && tapGap > 0) {
        if (clickTimer.current) {
          window.clearTimeout(clickTimer.current);
          clickTimer.current = null;
        }
        handleDoubleAction();
      } else {
        clickTimer.current = window.setTimeout(() => {
          handleSingleAction();
          clickTimer.current = null;
        }, 250);
      }
      lastTap.current = currentTime;
    };

    return (
      <Grid
        key={`cell-${layer}-${xAxis}-${yAxis}`}
        item
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{ backgroundColor: `${color} !important`, color: textColor }}
        className={`container-planner-grid ${
          isAnArrival ? "grid-arrival-container" : ""
        } ${
          container?.containerType?.teu > 1
            ? `container-planner-grid-teu-${container?.containerType?.teu}`
            : ""
        } ${
          ghostGrid ? "container-planner-ghost-grid" : ""
        } ${
          ghostGrid && 
          xAxis === grids.length && 
          gridCount === 3 ? "container-planner-ghost-grid--small" : ""
        }`}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        onTouchEnd={onTouchEnd}
      >
        {(
          (planType === PLAN_TYPE_LOADING && container?.loadedDate) ||
          (planType === PLAN_TYPE_UNLOADING && container?.unloadedDate)
        ) ? (
          <CheckCircleIcon
            sx={{
              stroke: "#ffffff",
              strokeWidth: 0.5,
              backgroundColor: "#ffffff",
              borderRadius: "100%",
            }}
            color="success"
            className="mobile-container-grid-icon"
            fontSize="large"
          />
        ) : (
          <Grid container position="relative" height="100%" alignItems="center">
            <Grid
              item
              sx={{
                display: "flex",
                gap: 1,
                position: "absolute",
                left: 0,
                bottom: 0,
              }}
            >
              <LabelDots labels={container?.labels} />
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  };

  const handleLayerChange = (_: React.SyntheticEvent, newValue: number) => {
    setSelectedLayer(newValue);
  };

  const onTouchStartLayer = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEndLayer = (e: React.TouchEvent<HTMLDivElement>) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diffX = touchStartX.current - touchEndX;
    const threshold = 30;
    const maxLayer = selectedVessel?.positions.length - 1 || 0;

    if (diffX > threshold && selectedLayer < maxLayer) {
      setSelectedLayer(selectedLayer + 1);
    } else if (diffX < -threshold && selectedLayer > 0) {
      setSelectedLayer(selectedLayer - 1);
    }
  };

  const handleRemainingCargoSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowRemainingCargo(event.target.checked);
  };

  return (
    <Grid
      item
      sx={{
        display: "flex",
        flexDirection: "column !important",
        justifyContent: "flex-start !important",
        padding: 0,
      }}
    >
      {selectedVessel?.positions ? (
        <>
          <Grid item sx={{ width: "100%" }}>
            <Tabs
              value={selectedLayer}
              onChange={handleLayerChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                width: "100%",
                minHeight: 36,
                backgroundColor: "#fff",
                borderBottom: "1px solid #e0e0e0",
                px: 0,
                "& .MuiTab-root": {
                  minHeight: 32,
                  minWidth: 60,
                  px: 1.5,
                  mx: 0.5,
                  fontSize: "0.75rem",
                  textTransform: "none",
                  flexShrink: 0,
                },
              }}
            >
              {selectedVessel.positions.map((_, idx) => {
                if (idx === 4) return null; //Temporary hide the 5th layer
                return <Tab key={`layer-tab-${idx}`} label={`L${idx + 1}`} value={idx} />;
              })}
            </Tabs>
          </Grid>
          <Grid
            container
            padding={1}
            paddingBottom={0}
            flexDirection="column"
            flexWrap="nowrap"
            alignItems="center"
            height="50px"
          >
            <Grid item>
              {selectedVessel ? (
                <ToggleButtonGroup
                  value={selectedVessel}
                  exclusive
                  onChange={selectedVesselForPlannerHandler}
                  sx={{
                    "& .MuiToggleButton-root": {
                      padding: "4px 8px",
                      fontSize: "0.75rem",
                    },
                  }}
                >
                  <ToggleButton size="small" value={selectedVesselForPlanner}>
                    {selectedVesselForPlanner?.name}
                  </ToggleButton>
                  {selectedVesselForPlanner?.linkedVessel && (
                    <ToggleButton
                      size="small"
                      value={selectedVesselForPlanner.linkedVessel}
                    >
                      {selectedVesselForPlanner.linkedVessel.name}
                    </ToggleButton>
                  )}
                </ToggleButtonGroup>
              ) : (
                ""
              )}
            </Grid>
            <Grid item>
              <Switch
                defaultChecked
                checked={showRemainingCargo}
                onChange={handleRemainingCargoSwitch}
              />
              Remaining Cargo
            </Grid>
          </Grid>
          <Grid container
            mt={2} 
            className="container-planner-main-wrapper"
            width="100%"
            onTouchStart={onTouchStartLayer}
            onTouchEnd={onTouchEndLayer}
            justifyContent="flex-end !important"
            sx={{
              height: "78vh !important"
            }}
          >
            <Grid
              key={`layer-${selectedLayer}`}
              className={`container-planner-wrapper container-planner-wrapper--vertical ${ selectedLayer === 4 ? 'hidden' : ''}`} //Temporary hide the 5th layer
              sx={{ padding: "0 !important" }}
            >
              <Grid className="container-planner-ship-bow">
                <Typography className="container-planner-layer-label">
                  {selectedLayer + 1}
                </Typography>
              </Grid>
              <Grid className="container-planner-ship-body">
                {selectedVessel.positions[selectedLayer]?.map(
                  (gridCount: number, x: number) => (
                    <React.Fragment key={`grid-x-${x}`}>
                      <Grid className="container-planner-ship-grid-wrapper container-planner-ship-grid-wrapper--reverse">
                        <Typography className="container-planner-x-axis-label">
                          {x + 1}
                        </Typography>
                        {[...Array(gridCount)].map((_, y) => (
                          <React.Fragment key={`cell-${x}-${y}`}>
                            {RenderGridCell(selectedLayer + 1, x + 1, y + 1, gridCount, selectedVessel.positions[selectedLayer])}
                          </React.Fragment>
                        ))}
                      </Grid>
                      {hasPositionBarrier(
                        [x + 1, x + 2],
                        selectedVessel
                      ) && <Grid className="container-planner-barrier" />}
                    </React.Fragment>
                  )
                )}
              </Grid>
            </Grid>
            <Typography component="span" variant="h6" sx={{
              position: "absolute",
              bottom: "6px",
              right: "14px",
              color: "#1976D2"
            }}>
                { planType === PLAN_TYPE_LOADING ? "Load" : "Unload" }
              </Typography>
          </Grid>
        </>
      ) : (
        <Grid item sx={{ display: "flex", justifyContent: "center" }}>
          <Alert severity="warning">
            Grid positions for this vessel are not set. Please contact the
            administrator.
          </Alert>
        </Grid>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>{activeContainer?.uid ?? "N/A"}</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: "30vh",
          }}
        >
          {activeContainer && (
            <>
              <Typography component="span" variant="body2">
                {`Weight: ${activeContainer.weight || "—"}`}
              </Typography>
              <Typography component="span" variant="body2">
                {`Type: ${activeContainer.containerType?.name || "—"}`}
              </Typography>
              <Typography
                component="span"
                variant="body2"
                display="flex"
                alignItems="center"
                gap={1}
                fontWeight={700}
              >
                {activeContainer.loadingTerminal?.name}
                <ArrowRightAltIcon fontSize="small" color="primary" />
                {activeContainer.unloadingTerminal?.name}
              </Typography>
              {activeContainer.labels?.length > 0 ? (
                <Grid container alignItems="center" mt={2}>
                  {RenderLabels(activeContainer.labels)}
                </Grid>
              ) : (
                <Grid />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Grid container justifyContent="space-between">
            {planType === PLAN_TYPE_LOADING && activeContainer ? (
              <Button
                variant="contained"
                onClick={() => {
                  if (!activeContainer) return;
                  const newLoadedDate = activeContainer.loadedDate
                    ? null
                    : new Date().toISOString();
                  dispatch(
                    updateSelectedContainer({
                      ...activeContainer,
                      loadedDate: newLoadedDate,
                    })
                  ).then(() => {
                    setOpenDialog(false);
                  });
                }}
                color="primary"
              >
                {activeContainer.loadedDate
                  ? "Unmark loaded"
                  : "Mark as loaded"}
              </Button>
            ) : planType === PLAN_TYPE_UNLOADING && activeContainer ? (
              <Button
                variant="contained"
                onClick={() => {
                  if (!activeContainer) return;
                  const newUnloadedDate = activeContainer.unloadedDate
                    ? null
                    : new Date().toISOString();
                  dispatch(
                    updateSelectedContainer({
                      ...activeContainer,
                      unloadedDate: newUnloadedDate,
                    })
                  ).then(() => {
                    setOpenDialog(false);
                  });
                }}
                color="primary"
              >
                {activeContainer.unloadedDate
                  ? "Unmark unloaded"
                  : "Mark as unloaded"}
              </Button>
            ) : (
              <Grid item />
            )}
            <Button onClick={() => setOpenDialog(false)} color="primary">
              Close
            </Button>
          </Grid>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default TerminalCallGrid;